import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  DataColumn,
  DataTableComponent,
  DataTablePage,
} from '../data-table/data-table.component';
import { matchesSearch } from '../../../core/utils/search-text';

/** Configuration de colonne héritée (compatible avec l'ancienne grille). */
interface LegacyColumn {
  /** Identifiant stable de la colonne, pour associer un gabarit de cellule.
   *  À défaut, `field` est utilisé. Requis pour les colonnes sans `field`. */
  key?: string;
  field?: string;
  headerText?: string;
  width?: number | string;
  textAlign?: string;
  template?: string | null;
  freeze?: string;
  /** Colonne secondaire masquée sous un seuil de largeur disponible. */
  hideBelow?: 'md' | 'lg';
  [k: string]: unknown;
}

/**
 * Tableau générique — **API historique conservée**, moteur remplacé.
 *
 * <p>Ce composant exposait auparavant une grille Syncfusion. Il rend désormais
 * le {@link DataTableComponent} partagé (même style que la banque de questions,
 * jetons de thème → 3 tiers + mode sombre, RTL natif) tout en gardant
 * exactement la même interface (`data`, `columns`, `actionTemplate`, `toolbar`,
 * `pageSettings`, événements). Tous les écrans qui l'utilisent migrent ainsi
 * sans être modifiés.</p>
 *
 * <p>Recherche, tri et pagination sont faits **côté client** — l'équivalent de
 * ce que fournissait la barre d'outils « Search » + le paging de la grille.</p>
 */
@Component({
  selector: 'app-custom-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule, DataTableComponent],
  templateUrl: './custom-tab.component.html',
  styleUrl: './custom-tab.component.css',
})
export class CustomTabComponent {
  // `any` conservé volontairement sur l'API publique : les appelants passent
  // `$event` à des handlers typés (Theme, Professor…). Un type plus strict
  // casserait leur compilation. Le typage interne reste, lui, prudent.
  @Input() data: any[] = [];
  @Input() columns: LegacyColumn[] = [];
  @Input() toolbar: string[] = ['Search'];
  @Input() pageSettings: {
    pageSize?: number;
    pageCount?: number;
    pageSizes?: number[];
  } = {
    pageSize: 10,
    pageCount: 5,
  };
  @Input() enableRtl = true;
  @Input() locale = 'ar';
  @Input() actionTemplate?: TemplateRef<any>;
  /** Gabarits de cellule par identifiant de colonne (`key` ou `field`).
   *  Permet les cellules riches (badges, dates formatées, contenu conditionnel)
   *  tout en gardant recherche / tri / pagination du composant. */
  @Input() cellTemplates: Record<string, TemplateRef<any>> = {};
  /**
   * L'appelant a déjà filtré `data` (barre de filtres de l'écran). Une liste
   * vide veut alors dire « rien ne correspond », pas « rien n'existe ».
   */
  @Input() filteredByCaller = false;

  @Output() rowClick = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() show = new EventEmitter<any>();

  search = '';
  currentPage = 1;
  /** Taille choisie dans le sélecteur ; `null` tant qu'on suit la configuration. */
  private chosenPageSize: number | null = null;
  sortBy: string | null = null;
  sortDescending = false;

  constructor(public translate: TranslateService) {}

  /* ── Configuration dérivée ───────────────────────────────── */

  get showSearch(): boolean {
    return (this.toolbar ?? []).includes('Search');
  }

  get pageSize(): number {
    return this.chosenPageSize ?? this.configuredPageSize;
  }

  private get configuredPageSize(): number {
    return this.pageSettings?.pageSize || 10;
  }

  /**
   * Tailles proposées, dérivées de la configuration et jamais de la taille
   * choisie : calculées sur cette dernière, elles glissaient à chaque choix
   * (10 → 20 proposait 20/40/100) et la taille de départ devenait inatteignable.
   */
  get pageSizeOptions(): number[] {
    const declared = this.pageSettings?.pageSizes;
    if (declared?.length) return declared;
    const base = this.configuredPageSize;
    return [base, base * 2, base * 5];
  }

  /** Colonnes traduites en {@link DataColumn} pour la table partagée. */
  get dtColumns(): DataColumn[] {
    return (this.columns ?? []).map((c) => ({
      key: this.columnKey(c),
      // headerText est déjà traduit par l'appelant ; `| translate` le renvoie tel
      // quel s'il ne correspond à aucune clé.
      labelKey: c.headerText ?? '',
      // Triable seulement si adossée à un champ (une cellule purement calculée
      // n'a pas d'ordre naturel).
      sortable: !this.isActionColumn(c) && !!c.field,
      align:
        c.textAlign?.toLowerCase() === 'center'
          ? 'center'
          : c.textAlign?.toLowerCase() === 'right'
            ? 'end'
            : 'start',
      // Colonne d'actions épinglée à l'inline-end (visible sans scroll).
      action: this.isActionColumn(c),
      // Colonnes secondaires masquées selon la largeur disponible.
      hideBelow: c.hideBelow,
    }));
  }

  columnKey(col: LegacyColumn): string {
    return col.key ?? col.field ?? '';
  }

  isActionColumn(col: LegacyColumn): boolean {
    return col.template === 'actionTemplate';
  }

  /** Gabarit de cellule fourni pour cette colonne, s'il y en a un. */
  cellTemplateFor(col: LegacyColumn): TemplateRef<any> | null {
    return this.cellTemplates?.[this.columnKey(col)] ?? null;
  }

  /* ── Données : filtre → tri → page ───────────────────────── */

  private resolve(row: unknown, field?: string): unknown {
    if (!field) return '';
    return field
      .split('.')
      .reduce<unknown>(
        (o, k) =>
          o == null ? o : (o as Record<string, unknown>)[k],
        row,
      );
  }

  cellValue(row: unknown, col: LegacyColumn): string {
    const v = this.resolve(row, col.field);
    return v == null ? '' : String(v);
  }

  /** Une recherche est-elle posée ? Distingue « rien ne correspond » de « rien du tout ». */
  get isSearching(): boolean {
    return this.search.trim().length > 0;
  }

  /** L'état vide tient-il à un filtre — le sien ou celui de l'écran ? */
  get emptyBecauseFiltered(): boolean {
    return this.isSearching || this.filteredByCaller;
  }

  private get filtered(): unknown[] {
    const rows = this.data ?? [];
    if (!this.isSearching) return rows;
    const fields = (this.columns ?? [])
      .filter((c) => c.field && !this.isActionColumn(c))
      .map((c) => c.field as string);
    // Champ par champ : un terme ne doit pas se trouver à cheval sur deux
    // colonnes sans rapport (le titre d'un test et sa matière).
    return rows.filter((row) =>
      fields.some((f) => matchesSearch(this.search, this.resolve(row, f))),
    );
  }

  /**
   * Champ de données d'une colonne triée.
   *
   * <p>La table émet la **clé** de la colonne, qui n'est pas toujours son
   * champ (`{ key: 'student', field: 'studentFullName' }`). Trier sur la clé
   * lisait alors une propriété absente de la ligne : l'en-tête changeait de
   * flèche et l'ordre ne bougeait pas.</p>
   */
  private sortField(key: string): string {
    const column = (this.columns ?? []).find((c) => this.columnKey(c) === key);
    return column?.field ?? key;
  }

  private get sorted(): unknown[] {
    const list = this.filtered;
    if (!this.sortBy) return list;
    const field = this.sortField(this.sortBy);
    const dir = this.sortDescending ? -1 : 1;
    return [...list].sort(
      (a, b) => this.compare(this.resolve(a, field), this.resolve(b, field)) * dir,
    );
  }

  /** Les nombres se comparent en nombres (9,5 avant 10), le reste selon la langue. */
  private compare(a: unknown, b: unknown): number {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a ?? '').localeCompare(String(b ?? ''), this.locale, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  get total(): number {
    return this.filtered.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get clampedPage(): number {
    return Math.min(this.currentPage, this.totalPages);
  }

  get pagedRows(): unknown[] {
    const start = (this.clampedPage - 1) * this.pageSize;
    return this.sorted.slice(start, start + this.pageSize);
  }

  get page(): DataTablePage {
    return {
      currentPage: this.clampedPage,
      totalPages: this.totalPages,
      total: this.total,
    };
  }

  /* ── Interactions ────────────────────────────────────────── */

  onSort(key: string): void {
    if (this.sortBy === key) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortBy = key;
      this.sortDescending = false;
    }
    this.currentPage = 1;
  }

  onPage(page: number): void {
    this.currentPage = page;
  }

  onPageSize(size: number): void {
    this.chosenPageSize = size;
    this.currentPage = 1;
  }

  onSearch(value: string): void {
    this.search = value;
    this.currentPage = 1;
  }

  /* Rétrocompat : émetteurs conservés pour les appelants qui les câblent. */
  onEdit(row: unknown): void {
    this.edit.emit(row);
  }
  onDelete(row: unknown): void {
    this.delete.emit(row);
  }
  onShow(row: unknown): void {
    this.show.emit(row);
  }
}
